import { useState } from 'react';
import { Modal, Button, Form, Alert, ListGroup } from 'react-bootstrap';
import { useForm } from '@tanstack/react-form';
import { UserService } from '@/services/user.service';
import { PlaylistService } from '@/services/playlist.service';
import { useAuth } from '@/hooks/useAuth';
import type { User, Playlist } from '@/types';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { emailValidator } from '@/lib/forms';

interface CollaboratorsModalProps {
    show: boolean;
    onHide: () => void;
    playlist: Playlist;
    currentCollaboratorUsers: User[]; // Pre-fetched user profiles
}

export function CollaboratorsModal({ show, onHide, playlist, currentCollaboratorUsers }: CollaboratorsModalProps) {
    const { user: currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    const inviteForm = useForm({
        defaultValues: {
            email: '',
        },
        onSubmit: async ({ value }) => {
            if (!value.email.trim() || !currentUser) return;
            setError(null);
            setSuccessMsg(null);
            setIsLoading(true);

            try {
                // 1. Search User
                const user = await UserService.searchUserByEmail(value.email.trim());

                if (!user) {
                    setError('User not found. Please check the email address.');
                    return;
                }

                if (playlist.collaborators[user.id]) {
                    setError('User is already a collaborator.');
                    return;
                }

                // 2. Send Invite
                const { InviteService } = await import('@/services/invite.service');
                await InviteService.sendInvite(playlist.id, playlist.name, currentUser, user);

                setSuccessMsg(`Invitation sent to ${user.name || user.email}`);
                inviteForm.reset();
            } catch (err) {
                console.error(err);
                setError('Failed to send invitation.');
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleRemove = async (userId: string) => {
        if (!confirm('Remove this collaborator?')) return;
        try {
            await PlaylistService.removeCollaborator(playlist.id, userId);
        } catch (err) {
            console.error(err);
            setError('Failed to remove collaborator.');
        }
    };

    const isOwner = (userId: string) => playlist.ownerId === userId;

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Manage Collaborators</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {successMsg && <Alert variant="success">{successMsg}</Alert>}

                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        inviteForm.handleSubmit();
                    }}
                    className="mb-4"
                >
                    <Form.Group className="mb-3">
                        <Form.Label>Invite by Email</Form.Label>
                        <inviteForm.Field
                            name="email"
                            validators={{
                                onChange: emailValidator,
                            }}
                        >
                            {(field) => (
                                <>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="email"
                                            placeholder="user@example.com"
                                            value={field.state.value}
                                            onChange={e => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                        />
                                        <Button type="submit" disabled={isLoading}>
                                            {isLoading ? <LoadingSpinner /> : 'Invite'}
                                        </Button>
                                    </div>
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </>
                            )}
                        </inviteForm.Field>
                        <Form.Text className="text-muted">
                            User must already be registered on PlaylistR.
                        </Form.Text>
                    </Form.Group>
                </Form>

                <h6 className="mb-2">Current Collaborators</h6>
                <ListGroup variant="flush">
                    {currentCollaboratorUsers.map(user => (
                        <ListGroup.Item key={user.id} className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                                <div className="bg-secondary text-white rounded-circle d-flex justify-content-center align-items-center" style={{ width: 32, height: 32 }}>
                                    {user.name?.charAt(0) || user.email.charAt(0)}
                                </div>
                                <div>
                                    <div className="fw-bold small">{user.name || 'Unnamed User'}</div>
                                    <div className="small text-muted" style={{ fontSize: '0.8rem' }}>{user.email}</div>
                                </div>
                            </div>
                            <div>
                                {isOwner(user.id) ? (
                                    <span className="badge bg-primary">Owner</span>
                                ) : (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleRemove(user.id)}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </ListGroup.Item>
                    ))}
                    {currentCollaboratorUsers.length === 0 && (
                        <div className="text-center text-muted my-3">No collaborators yet.</div>
                    )}
                </ListGroup>
            </Modal.Body>
        </Modal>
    );
}
